/**
 * The time on a live's registration page, always right. The page designer
 * places the elements (`data-live="when-chip"`, `"when-long"`, `"when-short"`,
 * a `"countdown"` with `data-cd` cells, and a `"local"` line); the words in
 * them are filled from the live's own row every time the page is served, so a
 * changed time shows everywhere at once. The board's preview fills the page
 * the same way, so the host sees what a visitor gets.
 *
 * The runtime ticks the countdown and writes the visitor's own time. It
 * carries the source of the label and countdown functions in `live-time.ts`,
 * so the page and the server say the time with the same code.
 *
 * A live that repeats (notes/recurring-lives-graph.md, R15) says its rhythm in
 * the same places, counts down to its next session, names that session's
 * topic on the local line, and once the series has ended lists every session
 * that ran where the countdown was.
 */
import { type Block, cutAt } from "./html-block";
import { liveWords } from "./language";
import type { LiveState } from "./live-protocol";
import { rhythmLabel, type SeriesRule, seriesChip, seriesLongLabel } from "./live-series";
import {
  countdownCells,
  labelParts,
  liveChipLabel,
  liveDateIn,
  liveLongLabel,
  liveShortLabel,
  localLineIn,
  localLineWith,
} from "./live-time";
import type { LiveSessionState } from "./types";

/** A series, as its page says it. */
interface LivePageSeries {
  /** `seriesChip`: "Every Thu · 7:00 PM India Time". */
  chip: string;
  /** `seriesLongLabel`: "Every Thursday · 7:00 PM India Time". */
  long: string;
  /** For inside a sentence: "every Thursday at 7:00 PM India Time". */
  short: string;
  /** The next session to come (UTC ISO) and its topic; null when none is. */
  next: { startsAt: string; topic: string | null } | null;
  /** No session is to come: the page lists the ones that ran. */
  ended: boolean;
  /** The sessions that ran, oldest first, each with its replay's length. */
  library: Array<{ startsAt: string; topic: string | null; minutes: number }>;
}

/** A live's time as the page needs it. */
export interface LivePageTime {
  /** UTC ISO. For a series, the session it is on now (`seriesPageTime`). */
  startsAt: string;
  /** IANA. */
  timezone: string;
  /** For a series, "live" while one of its sessions is. */
  state: LiveState;
  series?: LivePageSeries;
  /** The room's language: the time is said in its words. Absent or English keeps the English wording. */
  language?: string;
}

/** One session of a series, as its page reads it. */
export interface LivePageSession {
  seq: number;
  state: LiveSessionState;
  /** UTC ISO. */
  startsAt: string;
  lengthMin: number;
  topic: string | null;
  /** The replay's length, once it has one. */
  durationSec?: number | null;
}

/** The rhythm for inside a sentence: "every Thursday at 7:00 PM India Time". */
export function seriesShortLabel(rule: SeriesRule): string {
  const rhythm = rhythmLabel(rule);
  const time = seriesLongLabel(rule).slice(rhythm.length + " · ".length);
  return `${rhythm.charAt(0).toLowerCase()}${rhythm.slice(1)} at ${time}`;
}

/**
 * A series' time as its page needs it: the session it is on (the live one,
 * else the next, else the last that ran), its rhythm in words, the next
 * session, and the sessions that ran.
 */
export function seriesPageTime(series: {
  rule: SeriesRule;
  sessions: readonly LivePageSession[];
  ended: boolean;
}): LivePageTime {
  const { rule } = series;
  const sessions = series.sessions.toSorted((a, b) => a.seq - b.seq);
  const live = sessions.find((s) => s.state === "live") ?? null;
  const next = series.ended ? null : (sessions.find((s) => s.state === "scheduled") ?? null);
  const ran = sessions.filter((s) => s.state === "ended");
  const current = live ?? next ?? ran.at(-1) ?? sessions.at(-1) ?? null;
  return {
    startsAt: current?.startsAt ?? "",
    timezone: rule.timezone,
    state: live ? "live" : next ? "scheduled" : "ended",
    series: {
      chip: seriesChip(rule),
      long: seriesLongLabel(rule),
      short: seriesShortLabel(rule),
      next: next ? { startsAt: next.startsAt, topic: next.topic } : null,
      ended: series.ended || (!live && !next),
      library: ran.map((s) => ({
        startsAt: s.startsAt,
        topic: s.topic,
        minutes: s.durationSec ? Math.max(1, Math.round(s.durationSec / 60)) : s.lengthMin,
      })),
    },
  };
}

/** The four cells a countdown carries, in the order it reads. */
const COUNTDOWN_CELLS = ["d", "h", "m", "s"] as const;

/** What the page says in place of the countdown once the live is over. */
function replayLine(long: string): string {
  return `This was live on ${long}. Register to watch the replay.`;
}

/** What the page says in place of the countdown while the host is live. */
export const LIVE_NOW_LINE = "Live now. Register and you're straight in.";

/** What the page says first once a series has ended. */
const SERIES_ENDED_LINE = "The series has ended.";

/**
 * The visitor's own time for a series' next session, and its day on their
 * calendar: "That's 9:30 AM your time. Next one Thursday, Sep 24: Pricing
 * videos." Empty when either zone is unreadable. Self-contained, with no
 * comments inside, for the same reason as `labelParts`: the runtime carries
 * its source.
 */
export function seriesLocalLineWith(
  parts: typeof labelParts,
  atIso: string,
  hostTz: string,
  visitorTz: string,
  topic: string,
): string {
  const mine = parts(atIso, visitorTz || undefined, "long", "long");
  const theirs = parts(atIso, hostTz, "long", "long");
  const day = parts(atIso, visitorTz || undefined, "short");
  if (!mine || !theirs || !day) return "";
  const same = mine.weekday === theirs.weekday && mine.time === theirs.time;
  const time = same ? "That's your time too." : `That's ${mine.time} your time.`;
  const about = topic ? `: ${topic}${/[.!?]$/.test(topic) ? "" : "."}` : ".";
  return `${time} Next one ${mine.weekday}, ${day.month} ${day.day}${about}`;
}

interface Found extends Block {
  tag: string;
  /** The element's own opening tag. */
  open: string;
}

/** Every element carrying `attr="value"`, outermost first, in page order. Nothing inside a style counts. */
function elementsWith(html: string, attr: "data-live" | "data-cd", value: string): Found[] {
  const re = new RegExp(`<([a-z][a-z0-9-]*)\\b[^>]*\\s${attr}="${value}"[^>]*>`, "gi");
  const styles = [...html.matchAll(/<style\b[\s\S]*?<\/style>/gi)].map((m) => [m.index, m.index + m[0].length]);
  const out: Found[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    const at = m.index;
    if (at < last || styles.some(([from, to]) => at >= from && at < to)) continue;
    const cut = cutAt(html, m[0], m[1], at);
    if (!cut) continue;
    out.push({ ...cut, tag: m[1], open: m[0] });
    last = cut.end;
  }
  return out;
}

const escapeText = (t: string) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escapeAttr = (t: string) => escapeText(t).replace(/"/g, "&quot;");

/** Replace every element found, from the last back, so the earlier offsets hold. */
function replaceEach(html: string, found: Found[], next: (el: Found) => string): string {
  let out = html;
  for (const el of found.toReversed()) out = out.slice(0, el.start) + next(el) + out.slice(el.end);
  return out;
}

const OPEN_TAG = /<([a-z][a-z0-9-]*)\b[^>]*>/gi;
const VOID_TAGS = new Set(["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "source", "track", "wbr"]);
/** Anything a visitor acts on, or reads as more than a label. */
const NOT_A_LABEL = /data-live|<(?:form|input|button|a|select|textarea|img|svg|video|iframe)\b/i;

/** The element that holds `el` most closely, or null. */
function parentOf(html: string, el: Found): Found | null {
  const opens = [...html.slice(0, el.start).matchAll(OPEN_TAG)];
  for (const m of opens.toReversed()) {
    if (VOID_TAGS.has(m[1].toLowerCase()) || m[0].endsWith("/>")) continue;
    const cut = cutAt(html, m[0], m[1], m.index);
    if (cut && cut.end >= el.end) return { ...cut, tag: m[1], open: m[0] };
  }
  return null;
}

/**
 * The element with the few words the designer put beside it ("We start in",
 * "Your time:"): its parent, when the parent holds nothing else. Those words
 * only make sense while there is a countdown, so they go with it.
 */
function withItsLabel(html: string, el: Found): { unit: Found; inner: (replaced: string) => string } {
  const alone = { unit: el, inner: (replaced: string) => replaced };
  const parent = parentOf(html, el);
  if (!parent) return alone;
  const close = parent.block.lastIndexOf("</");
  const rest = html.slice(parent.start + parent.open.length, el.start) + html.slice(el.end, parent.start + close);
  if (NOT_A_LABEL.test(rest)) return alone;
  const words = rest.replace(/<[^>]*>/g, " ").replace(/&[a-z0-9#]+;/gi, " ").trim();
  if (words.length > 40 || words.split(/\s+/).filter(Boolean).length > 4) return alone;
  return { unit: parent, inner: (replaced) => `${parent.open}${replaced}${parent.block.slice(close)}` };
}

/**
 * Replace each element, or the parent holding it and its label. `next` gives
 * the element's new markup, or null to take it away, label and all.
 */
function replaceUnits(html: string, found: Found[], next: (el: Found) => string | null): string {
  const units = found.map((el) => ({ el, ...withItsLabel(html, el) }));
  let out = html;
  for (const { el, unit, inner } of units.toReversed()) {
    const replaced = next(el);
    out = out.slice(0, unit.start) + (replaced === null ? "" : inner(replaced)) + out.slice(unit.end);
  }
  return out;
}

/** "60 minutes, live" once the live is over: the length stays, the word that is no longer true goes. */
const LENGTH_THEN_LIVE = /(\b\d+\s*(?:minutes?|mins?|hours?|hrs?))\s*,\s*live(?=\s*<)/gi;

/** The element with an opening tag and new words inside. */
function withInner(el: Found, open: string, inner: string): string {
  return `${open}${inner}</${el.tag}>`;
}

/**
 * The opening tag carrying the time the runtime reads, once, whatever the
 * model wrote there. A series' local line also carries the next session's
 * topic, empty when it has none, which is how the runtime knows it repeats.
 */
function stamped(open: string, startsAt: string, timezone: string, topic?: string, words?: { lang: string; same: string; mine: string }): string {
  const clean = open.replace(/\sdata-(?:starts-at|tz|topic|lang|same|mine)="[^"]*"/gi, "");
  const about = topic === undefined ? "" : ` data-topic="${escapeAttr(topic)}"`;
  const said = words ? ` data-lang="${escapeAttr(words.lang)}" data-same="${escapeAttr(words.same)}" data-mine="${escapeAttr(words.mine)}"` : "";
  return clean.replace(
    /\s*\/?>$/,
    ` data-starts-at="${escapeAttr(startsAt)}" data-tz="${escapeAttr(timezone)}"${about}${said}>`,
  );
}

/** Where the page is: counting down, live now, a one-off's replay, or a series' library. */
type PagePhase = "count" | "now" | "ended" | "library";

function phaseOf(time: LivePageTime): PagePhase {
  if (time.state === "live") return "now";
  if (!time.series) return time.state === "ended" ? "ended" : "count";
  return time.series.ended || !time.series.next ? "library" : "count";
}

/** "Sep 24" on the host's clock, or empty. */
function monthDay(atIso: string, tz: string): string {
  const p = labelParts(atIso, tz, "short");
  return p ? `${p.month} ${p.day}` : "";
}

/**
 * A series' library, where its countdown was: that it has ended, how many
 * sessions ran and when, and each one by its topic. Plain elements, so they
 * take the page's own fonts and colours.
 */
function libraryHtml(series: LivePageSeries, tz: string): string {
  const ran = series.library;
  const first = ran[0];
  const last = ran.at(-1);
  if (!first || !last) return `<div data-live="library"><p><strong>${SERIES_ENDED_LINE}</strong></p></div>`;
  const count = ran.length === 1 ? "1 live session" : `${ran.length} live sessions`;
  const dates = ran.length === 1 ? monthDay(first.startsAt, tz) : `${monthDay(first.startsAt, tz)} to ${monthDay(last.startsAt, tz)}`;
  const watch = ran.length === 1 ? "It's here to watch." : "Every one is here to watch.";
  const head = `<p><strong>${SERIES_ENDED_LINE}</strong><br>${escapeText(`${count}, ${dates}. ${watch}`)}</p>`;
  const items = ran.map((s) => {
    const p = labelParts(s.startsAt, tz, "short");
    const day = p ? `${p.weekday}, ${p.month} ${p.day}` : "";
    const detail = [s.topic ? day : "", `${s.minutes} min`].filter(Boolean).join(" · ");
    return `<p><strong>${escapeText(s.topic || day)}</strong><br>${escapeText(detail)}</p>`;
  });
  return `<div data-live="library">${head}${items.join("")}</div>`;
}

/**
 * The page with the live's time filled in: the three labels, the countdown's
 * cells as of `now` (the runtime takes over from there), the elements the
 * runtime reads stamped with the time and the zone, and the local line empty
 * for the runtime to write. While the host is live the countdown says so, and
 * after the live it says the replay is there; either way the local line goes,
 * and so do the few words labelling it or the countdown. After the live a
 * length no longer says "live". A series says its rhythm, counts down to its next session, and once it has
 * ended holds its library where the countdown was.
 */
export function liveFill(html: string, time: LivePageTime, now: number): string {
  const series = time.series;
  const labels = series
    ? { "when-chip": series.chip, "when-long": series.long, "when-short": series.short }
    : {
        "when-chip": liveChipLabel(time.startsAt, time.timezone, time.language),
        "when-long": liveLongLabel(time.startsAt, time.timezone, time.language),
        "when-short": liveShortLabel(time.startsAt, time.timezone, time.language),
      };
  // Another language says its own sentences; one with no words written says none rather than English ones.
  const other = time.language && time.language.split("-")[0].toLowerCase() !== "en" ? time.language : null;
  const said = other ? liveWords(other) : null;
  const spoken = other && said ? { lang: other, same: said.sameTime, mine: said.yourTime } : undefined;
  let out = html;
  for (const mark of ["when-chip", "when-long", "when-short"] as const) {
    const words = labels[mark];
    // An unreadable zone says nothing, so the words the model wrote stay.
    if (!words) continue;
    out = replaceEach(out, elementsWith(out, "data-live", mark), (el) => withInner(el, el.open, escapeText(words)));
  }

  // Why: once the host is live there is nothing to count down to, and the
  // visitor's own start time no longer helps them; joining does.
  const phase = phaseOf(time);
  const target = series?.next?.startsAt ?? time.startsAt;
  if (phase === "count") {
    out = replaceEach(out, elementsWith(out, "data-live", "countdown"), (el) => {
      const cells = countdownCells(Date.parse(target) - now);
      let block = el.block;
      for (const cell of COUNTDOWN_CELLS) {
        block = replaceEach(block, elementsWith(block, "data-cd", cell), (c) => withInner(c, c.open, cells[cell]));
      }
      return stamped(el.open, target, time.timezone) + block.slice(el.open.length);
    });
    const topic = series ? (series.next?.topic ?? "") : undefined;
    if (other && !spoken) return replaceUnits(out, elementsWith(out, "data-live", "local"), () => null);
    return replaceEach(out, elementsWith(out, "data-live", "local"), (el) =>
      withInner(el, stamped(el.open, target, time.timezone, other ? undefined : topic, spoken), ""),
    );
  }
  out = replaceUnits(out, elementsWith(out, "data-live", "countdown"), (el) => {
    if (phase === "library" && series) return libraryHtml(series, time.timezone);
    const mark = phase === "ended" ? "ended" : "now";
    const open = el.open.replace(/\sdata-live="countdown"/i, ` data-live="${mark}"`);
    if (other && !said) return null;
    const words = said
      ? phase === "ended"
        ? said.replay.replace("{date}", liveDateIn(time.startsAt, time.timezone, other as string))
        : said.liveNow
      : phase === "ended"
        ? replayLine(labels["when-long"])
        : LIVE_NOW_LINE;
    return withInner(el, open, escapeText(words));
  });
  out = replaceUnits(out, elementsWith(out, "data-live", "local"), () => null);
  return phase === "now" ? out : out.replace(LENGTH_THEN_LIVE, "$1");
}

/**
 * What runs on the page: the countdown ticks every second and each local line
 * says the visitor's own time. Plain script around the three functions from
 * `live-time.ts`, which reach nothing outside themselves.
 */
export const LIVE_RUNTIME = `(function(){
  var parts=${labelParts.toString()};
  var line=${localLineWith.toString()};
  var sline=${seriesLocalLineWith.toString()};
  var cells=${countdownCells.toString()};
  var lline=${localLineIn.toString()};
  var D=document,K=["d","h","m","s"];
  function zone(){try{return Intl.DateTimeFormat().resolvedOptions().timeZone||"";}catch(e){return "";}}
  function tick(){var cds=D.querySelectorAll('[data-live="countdown"][data-starts-at]');
    for(var i=0;i<cds.length;i++){var c=cells(Date.parse(cds[i].getAttribute("data-starts-at")||"")-Date.now());
      for(var k=0;k<K.length;k++){var n=cds[i].querySelectorAll('[data-cd="'+K[k]+'"]');for(var j=0;j<n.length;j++)n[j].textContent=c[K[k]];}}}
  function local(){var me=zone(),ls=D.querySelectorAll('[data-live="local"][data-starts-at]');
    for(var i=0;i<ls.length;i++){var l=ls[i],at=l.getAttribute("data-starts-at")||"",tz=l.getAttribute("data-tz")||"";
      if(l.hasAttribute("data-lang")){l.textContent=lline(at,tz,me,l.getAttribute("data-lang")||"en",l.getAttribute("data-same")||"",l.getAttribute("data-mine")||"{when}");continue;}
      l.textContent=l.hasAttribute("data-topic")?sline(parts,at,tz,me,l.getAttribute("data-topic")||""):line(parts,at,tz,me);}}
  function start(){local();tick();setInterval(tick,1000);}
  if(D.readyState==="loading")D.addEventListener("DOMContentLoaded",start);else start();
})();`;

/** The page, filled, with the runtime in its head while there is still something to count down to. */
export function withLive(html: string, time: LivePageTime, now: number): string {
  const filled = liveFill(html, time, now);
  if (phaseOf(time) !== "count") return filled;
  const tag = `<script data-live>${LIVE_RUNTIME}</script>`;
  if (/<\/head>/i.test(filled)) return filled.replace(/<\/head>/i, () => `${tag}</head>`);
  if (/<head[^>]*>/i.test(filled)) return filled.replace(/<head[^>]*>/i, (m) => `${m}${tag}`);
  return `${tag}${filled}`;
}

/** Why a live's page cannot count down or say the visitor's time, or null when it can. */
export function liveMarksMissing(html: string): string | null {
  const countdown = elementsWith(html, "data-live", "countdown")[0];
  if (!countdown) return "no countdown";
  if (COUNTDOWN_CELLS.some((cell) => elementsWith(countdown.block, "data-cd", cell).length === 0)) {
    return "a countdown cell is missing";
  }
  if (elementsWith(html, "data-live", "local").length === 0) return "no local time line";
  return null;
}

/** Every `data-live` and `data-cd` mark in a piece of the page, counted. Styles do not count. */
export function liveMarksIn(html: string): Map<string, number> {
  const bare = html.replace(/<style\b[\s\S]*?<\/style>/gi, "");
  const out = new Map<string, number>();
  for (const m of bare.matchAll(/\sdata-(live|cd)="([^"]+)"/gi)) {
    const key = `${m[1]}=${m[2]}`;
    out.set(key, (out.get(key) ?? 0) + 1);
  }
  return out;
}
